/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import {
  isPWA,
  canInstallPWA,
  installPWA,
  installPWAWithPrompt
} from "@/utilities/pwa-utils";
import { config } from "@/config/environment";
import type { PWAInstallState } from "@/types/core";
import { reportPwaInstalled } from "@/pwa/report-pwa-installed";
import { SHOW_PWA_INSTALL_UI } from "@/pwa/pwa-install-flags";

type Ctx = PWAInstallState & {
  /** Browser has provided `beforeinstallprompt` (Chrome, Edge, etc.). */
  hasDeferredPrompt: boolean;
  /** Show “Install app” in the shell when not running as an installed PWA. */
  showSidebarInstallCta: boolean;
  showInstallPrompt: () => void;
  hideInstallPrompt: () => void;
  handleInstall: () => Promise<void>;
};

const PwaInstallContext = createContext<Ctx | null>(null);

function getGlobalDeferred():
  | BeforeInstallPromptEvent
  | undefined {
  return (window as Window & { __deferredPrompt?: BeforeInstallPromptEvent })
    .__deferredPrompt;
}

export function PwaInstallProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PWAInstallState>(() => {
    const installed = isPWA();
    return {
      canInstall: canInstallPWA() && !installed,
      isInstalled: installed,
      isInstalling: false,
      showPrompt: false,
      deferredPrompt: null
    };
  });

  const hasDeferredPrompt = Boolean(state.deferredPrompt || getGlobalDeferred());
  const showSidebarInstallCta = SHOW_PWA_INSTALL_UI && !state.isInstalled;

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const ev = e as BeforeInstallPromptEvent;
      (window as Window & { __deferredPrompt?: BeforeInstallPromptEvent }).__deferredPrompt =
        ev;

      if (config.debug.enabled) {
        console.log("PWA install prompt available");
      }

      setState((prev) => ({
        ...prev,
        deferredPrompt: ev,
        canInstall: true
      }));
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, []);

  useEffect(() => {
    const handleAppInstalled = () => {
      if (config.debug.enabled) {
        console.log("PWA installed successfully");
      }

      reportPwaInstalled();

      delete (
        window as Window & { __deferredPrompt?: BeforeInstallPromptEvent }
      ).__deferredPrompt;

      setState({
        isInstalled: true,
        canInstall: false,
        isInstalling: false,
        showPrompt: false,
        deferredPrompt: null
      });
    };

    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const showInstallPrompt = useCallback(() => {
    setState((prev) => {
      if (prev.isInstalled) {
        return prev;
      }
      return { ...prev, showPrompt: true };
    });
  }, []);

  const hideInstallPrompt = useCallback(() => {
    setState((prev) => ({ ...prev, showPrompt: false }));
  }, []);

  const handleInstall = useCallback(async () => {
    setState((prev) => ({ ...prev, isInstalling: true }));

    const promptToUse = getGlobalDeferred() ?? state.deferredPrompt;

    if (config.debug.enabled) {
      console.log("PWA install attempt:", {
        hasDeferredPrompt: Boolean(promptToUse),
        isInstalled: isPWA()
      });
    }

    try {
      if (promptToUse) {
        const success = await installPWAWithPrompt(promptToUse);

        if (success) {
          delete (
            window as Window & { __deferredPrompt?: BeforeInstallPromptEvent }
          ).__deferredPrompt;
          setState((prev) => ({
            ...prev,
            isInstalled: true,
            showPrompt: false,
            isInstalling: false,
            deferredPrompt: null,
            canInstall: false
          }));
        } else {
          if (config.debug.enabled) {
            console.log("PWA installation dismissed");
          }
          setState((prev) => ({
            ...prev,
            isInstalling: false,
            showPrompt: false
          }));
        }
      } else {
        if (config.debug.enabled) {
          console.log("Using fallback install method");
        }
        const success = await installPWA();
        if (success) {
          setState((prev) => ({
            ...prev,
            isInstalled: true,
            showPrompt: false,
            isInstalling: false,
            deferredPrompt: null,
            canInstall: false
          }));
        } else {
          setState((prev) => ({ ...prev, isInstalling: false }));
        }
      }
    } catch (error) {
      if (config.debug.enabled) {
        console.error("PWA installation failed:", error);
      }
      setState((prev) => ({ ...prev, isInstalling: false }));
    }
  }, [state.deferredPrompt]);

  const value = useMemo<Ctx>(
    () => ({
      ...state,
      hasDeferredPrompt,
      showSidebarInstallCta,
      showInstallPrompt,
      hideInstallPrompt,
      handleInstall
    }),
    [
      state,
      hasDeferredPrompt,
      showSidebarInstallCta,
      showInstallPrompt,
      hideInstallPrompt,
      handleInstall
    ]
  );

  return (
    <PwaInstallContext.Provider value={value}>
      {children}
    </PwaInstallContext.Provider>
  );
}

export function usePWAInstall(): Ctx {
  const ctx = useContext(PwaInstallContext);
  if (!ctx) {
    throw new Error("usePWAInstall must be used within PwaInstallProvider");
  }
  return ctx;
}
